import { Button, Spinner } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";
import { BrandLogo } from "@webuild-ai/react-ui/components";

import DefaultLayout from "@/layouts/default"; // Import DefaultLayout
import { title } from "@/components/primitives"; // Import title
import { useEmbeddedAuthConfig } from "@/providers/EmbeddedAuthProvider";

const LoginPrompt = () => {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const { brand } = useBrand();
  const { isEmbedded, status, error, requestAuth } = useEmbeddedAuthConfig();

  // Embedded (iframe) mode: interactive login cannot run inside the frame, so instead of
  // the Login button we surface the handshake status. While the parent is still being
  // asked for authentication we show a spinner; on a clean sign-out from the parent we say
  // so; if it never responds we explain how to recover and offer a retry that re-requests
  // a token from the parent.
  const renderEmbeddedBody = () => {
    if (status === "logged-out") {
      return (
        <div className="login-prompt">
          <div className="login-content flex flex-col items-center gap-4">
            <p className="text-default-500 text-sm">{t("You have been signed out.")}</p>
            <Button
              className="btn-brand-primary text-sm"
              data-id="embedded-retry-button"
              size="lg"
              onPress={() => requestAuth()}
            >
              {t("Sign in again")}
            </Button>
          </div>
        </div>
      );
    }

    if (status === "failed" || error) {
      return (
        <div className="login-prompt">
          <div className="login-content flex flex-col items-center gap-4">
            <p className="text-danger text-sm">{t("We could not sign you in automatically.")}</p>
            <p className="text-default-500 text-xs">
              {t("Please open this application from the host platform, then try again.")}
            </p>
            <Button
              className="btn-brand-primary text-sm"
              data-id="embedded-retry-button"
              size="lg"
              onPress={() => requestAuth()}
            >
              {t("Try again")}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="login-prompt">
        <div className="login-content flex flex-col items-center gap-4">
          <Spinner aria-label={t("Signing you in")} />
          <p className="text-default-500 text-sm">{t("Signing you in…")}</p>
        </div>
      </div>
    );
  };

  return (
    <DefaultLayout>
      <section className="flex flex-col h-full items-center justify-center gap-4 py-8 md:py-10">
        {brand.name && (
          <div className="inline-block max-w-lg  text-center justify-center">
            {brand.layout.showLogo ? (
              <BrandLogo
                className="mx-auto mb-10"
                style={{ height: brand.layout.menuItemsIconSize ?? 60 }}
              />
            ) : (
              <>
                <span className={title()}>{brand.name}&nbsp;</span>
                <span className={`${title()} text-primary`}>{"Pathway"}&nbsp;</span>
              </>
            )}
            {isEmbedded ? (
              renderEmbeddedBody()
            ) : (
              <div className="login-prompt">
                <div className="login-content">
                  <Button
                    className="btn-brand-primary text-sm"
                    data-id="login-button"
                    size="lg"
                    onPress={() => void loginWithRedirect()}
                  >
                    {t("Login")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </DefaultLayout>
  );
};

export default LoginPrompt;
